import { Injectable } from '@angular/core';

export interface AssetExportFilterState {
  selectedProjectIds: string[];
  selectedFocusValues: string[];
  selectedTypeValues: string[];
  selectedBetsValues: string[];
  selectedDurationValues: string[];
  selectedFormatValues: string[];
  selectedCategoriesValues: string[];
  selectedInUseValues: string[];
  selectedStatusValues: string[];
  selectedReleaseDateValues: string[];
  showHiddenOnly: boolean;
}

@Injectable()
export class AssetExportFilterService {
  public filterAssets(assets: any[], filterState: AssetExportFilterState, affectedAssets: Record<string, any>): any[] {
    if (!assets?.length) {
      return [];
    }
    return assets.filter((asset: any) =>
      this.assetMatchesAllFilters(asset as Record<string, any>, filterState, affectedAssets)
    );
  }

  private assetMatchesAllFilters(
    asset: Record<string, any>,
    state: AssetExportFilterState,
    affectedAssets: Record<string, any>
  ): boolean {
    return (
      this.filterByProject(asset, state.selectedProjectIds) &&
      this.filterByFocus(asset, state.selectedFocusValues) &&
      this.filterByType(asset, state.selectedTypeValues) &&
      this.filterByBets(asset, state.selectedBetsValues) &&
      this.filterByDuration(asset, state.selectedDurationValues) &&
      this.filterByFormat(asset, state.selectedFormatValues) &&
      this.filterByCategories(asset, state.selectedCategoriesValues) &&
      this.filterByHidden(asset, state.showHiddenOnly) &&
      this.filterByInUse(asset, state.selectedInUseValues, affectedAssets) &&
      this.filterByStatus(asset, state.selectedStatusValues, affectedAssets) &&
      this.filterByReleaseDate(asset, state.selectedReleaseDateValues)
    );
  }

  private filterByProject(asset: Record<string, any>, selectedProjectIds: string[]): boolean {
    if (!selectedProjectIds?.length) {
      return true;
    }
    return selectedProjectIds.includes(asset?.projectId as string);
  }

  private filterByFocus(asset: Record<string, any>, selectedFocusValues: string[]): boolean {
    if (!selectedFocusValues?.length) {
      return true;
    }
    return asset?.focus ? selectedFocusValues.includes(asset.focus as string) : false;
  }

  private filterByType(asset: Record<string, any>, selectedTypeValues: string[]): boolean {
    if (!selectedTypeValues?.length) {
      return true;
    }
    return asset?.type ? selectedTypeValues.includes(asset.type as string) : false;
  }

  private filterByBets(asset: Record<string, any>, selectedBetsValues: string[]): boolean {
    if (!selectedBetsValues?.length) {
      return true;
    }
    if (!asset?.bets || !Array.isArray(asset.bets) || !asset.bets.length) {
      return false;
    }
    return (asset.bets as string[]).some((bet) => selectedBetsValues.includes(bet));
  }

  private filterByDuration(asset: Record<string, any>, selectedDurationValues: string[]): boolean {
    if (!selectedDurationValues?.length) {
      return true;
    }
    if (!asset?.length || !Array.isArray(asset.length) || !asset.length.length) {
      return false;
    }
    return (asset.length as string[]).some((len) => this.matchesDurationPrefix(len, selectedDurationValues));
  }

  private matchesDurationPrefix(len: string, selectedDurationValues: string[]): boolean {
    return selectedDurationValues.some((duration) => len.toUpperCase().startsWith(duration.toUpperCase()));
  }

  private filterByFormat(asset: Record<string, any>, selectedFormatValues: string[]): boolean {
    if (!selectedFormatValues?.length) {
      return true;
    }
    if (!asset?.format || !Array.isArray(asset.format) || !asset.format.length) {
      return false;
    }
    return (asset.format as string[]).some((fmt) => selectedFormatValues.includes(fmt));
  }

  private filterByCategories(asset: Record<string, any>, selectedCategoriesValues: string[]): boolean {
    if (!selectedCategoriesValues?.length) {
      return true;
    }
    if (!asset?.categories || !Array.isArray(asset.categories)) {
      return false;
    }
    return (asset.categories as string[]).some((cat) => selectedCategoriesValues.includes(cat));
  }

  private filterByHidden(asset: Record<string, any>, showHiddenOnly: boolean): boolean {
    if (!showHiddenOnly) {
      return true;
    }
    return asset.hidden === true;
  }

  private filterByInUse(
    asset: Record<string, any>,
    selectedInUseValues: string[],
    affectedAssets: Record<string, any>
  ): boolean {
    if (!selectedInUseValues?.length) {
      return true;
    }
    const isInUse: boolean = !!affectedAssets[asset.id as string];
    const hasYes: boolean = selectedInUseValues.includes('YES');
    const hasNo: boolean = selectedInUseValues.includes('NO');
    if (hasYes && hasNo) {
      return true;
    }
    return hasYes ? isInUse : !isInUse;
  }

  private filterByStatus(
    asset: Record<string, any>,
    selectedStatusValues: string[],
    affectedAssets: Record<string, any>
  ): boolean {
    if (!selectedStatusValues?.length) {
      return true;
    }
    const affectedAsset = affectedAssets[asset.id as string] as { status?: string } | undefined;
    const status: string = affectedAsset ? affectedAsset.status || 'any' : 'NOT_AFFECTED';
    return selectedStatusValues.includes(status);
  }

  private filterByReleaseDate(asset: Record<string, any>, selectedReleaseDateValues: string[]): boolean {
    if (!selectedReleaseDateValues?.length) {
      return true;
    }
    if (!asset?.releaseDate) {
      return false;
    }
    return this.matchesReleaseDate(asset.releaseDate as string, selectedReleaseDateValues);
  }

  private matchesReleaseDate(releaseDate: string, selectedValues: string[]): boolean {
    try {
      const date = new Date(Number.parseInt(releaseDate));
      const yearMonth = this.formatYearMonth(date);
      return selectedValues.includes(yearMonth);
    } catch {
      return false;
    }
  }

  private formatYearMonth(date: Date): string {
    const year = date.getFullYear().toString();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }
}
